import { createContext, useContext, type ReactNode } from "react";
import type { EventOfType, EventType, RegisterSettings } from "@shared/events";
import type { RegisterState } from "@shared/ledger";

/**
 * Seam with C1's `@client/state/registerStore` (D-C2-3).
 *
 * The Sell screen consumes the documented `useRegister()` shape. C1's module is built in parallel, so
 * it is picked up through `import.meta.glob` (a static import of a file that does not exist yet would
 * break this package's typecheck and tests). Order of resolution:
 *   1. an explicit `register` prop — how the tests inject a fake;
 *   2. `SellRegisterContext` — how a host that already has the store can inject it;
 *   3. `useRegister()` from `@client/state/registerStore`, as soon as that module exists.
 * Once C1 has landed, the coordinator can replace this file's `upstreamUseRegister` with a plain import.
 */
export interface SellRegister {
  state: RegisterState;
  dispatch<T extends EventType>(type: T, payload: EventOfType<T>["payload"]): unknown;
  settings: RegisterSettings;
  syncStatus?: string;
  pendingCount?: number;
  /** C1 exposes this directly; `state.registerId` is null until the first event is folded. */
  registerId?: string;
  /** Cart draft (F-409). Provided either directly or via the store the register wraps. */
  saveCartDraft?(registerId: string, draft: unknown): unknown;
  loadCartDraft?(registerId: string): unknown;
  store?: {
    saveCartDraft(registerId: string, draft: unknown): Promise<void>;
    loadCartDraft(registerId: string): Promise<unknown | null>;
  };
}

export const SellRegisterContext = createContext<SellRegister | null>(null);

export function SellRegisterProvider(props: { value: SellRegister; children: ReactNode }) {
  return <SellRegisterContext.Provider value={props.value}>{props.children}</SellRegisterContext.Provider>;
}

type UpstreamModule = { useRegister?: () => SellRegister };

const upstreamModules = import.meta.glob<UpstreamModule>(
  ["../../state/registerStore.tsx", "../../state/registerStore.ts"],
  { eager: true },
);

/** C1's `useRegister`, or undefined while that package is still in flight. */
export const upstreamUseRegister: (() => SellRegister) | undefined = (() => {
  for (const mod of Object.values(upstreamModules)) {
    if (mod && typeof mod.useRegister === "function") return mod.useRegister;
  }
  return undefined;
})();

export class MissingRegisterError extends Error {
  constructor() {
    super(
      "Sell screen has no register. Pass `register`, wrap in <SellRegisterProvider>, " +
        "or provide useRegister() from @client/state/registerStore.",
    );
    this.name = "MissingRegisterError";
  }
}

/**
 * Reads the injected register. Hook order is stable: whether the upstream hook exists is a module
 * constant, so it is consulted on every render, and a throw from it (no provider mounted) falls
 * through to the injected value instead of taking down the screen.
 */
export function useSellRegister(injected?: SellRegister): SellRegister {
  const fromContext = useContext(SellRegisterContext);
  let fromUpstream: SellRegister | null = null;
  if (upstreamUseRegister) {
    try {
      fromUpstream = upstreamUseRegister();
    } catch {
      fromUpstream = null;
    }
  }
  const reg = injected ?? fromContext ?? fromUpstream;
  if (!reg) throw new MissingRegisterError();
  return reg;
}

/** Cart draft I/O, wherever the store exposed it. Absent methods degrade to a non-persisted cart. */
export function cartDraftIo(reg: SellRegister) {
  const save = reg.saveCartDraft?.bind(reg) ?? reg.store?.saveCartDraft.bind(reg.store);
  const load = reg.loadCartDraft?.bind(reg) ?? reg.store?.loadCartDraft.bind(reg.store);
  return {
    save: (registerId: string, draft: unknown): void => {
      try {
        void Promise.resolve(save?.(registerId, draft)).catch(() => {});
      } catch {
        /* a draft that cannot be written must never interrupt a sale */
      }
    },
    load: async (registerId: string): Promise<unknown> => {
      try {
        return await Promise.resolve(load?.(registerId) ?? null);
      } catch {
        return null;
      }
    },
    available: Boolean(save && load),
  };
}
